import { Check, ChevronsUpDown, History } from 'lucide-react'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  continents,
  getClusterContinent,
} from '@/components/form/location-selector/continents'
import { LocationSelectorFallback } from '@/components/form/location-selector/fallback'
import { findClosestCluster } from '@/components/form/location-selector/find-closest-cluster'
import { useClusters } from '@/components/form/location-selector/use-clusters'
import { ProviderLogo } from '@/components/shared/provider-logo'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'

type Props = {
  fieldKey?: string
}

const LocationSelectorContent = ({ fieldKey = 'idCluster' }: Props) => {
  const [open, setOpen] = useState(false)
  const form = useFormContext<{ idCluster: string }>()
  const { watch, setValue } = form
  const idCluster = watch(fieldKey as any)
  const { rbac } = useRBAC()
  const isSuperadmin = rbac.kinsta().superadmin.can()

  const { clusters, recentClusters } = useClusters()

  const selectedCluster = useMemo(() => {
    return clusters.find((cluster) => cluster.id === idCluster)
  }, [clusters, idCluster])

  const closestCluster = useMemo(() => {
    return findClosestCluster(clusters)
  }, [clusters])

  const groupedClusters = useMemo(
    () =>
      continents
        .map((continent) => {
          const continentClusters = clusters.filter(
            (cluster) =>
              getClusterContinent(cluster.location) === continent.code
          )

          return {
            continent,
            clusters: continentClusters.sort(
              (a, b) => a.displayName?.localeCompare(b.displayName ?? '') ?? 0
            ),
          }
        })
        .filter(({ clusters }) => clusters.length),
    [clusters]
  )

  useEffect(() => {
    if (closestCluster && !idCluster) {
      setValue(fieldKey as any, closestCluster.id)
    }
  }, [closestCluster, idCluster, setValue, fieldKey])

  return (
    <FormField
      control={form.control}
      name={fieldKey as any}
      render={({ field }) => (
        <FormItem className="w-full min-w-0">
          <FormLabel>Location</FormLabel>
          <FormControl>
            <div className="flex flex-col gap-4 w-full min-w-0">
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent min-w-0"
                  >
                    {selectedCluster ? (
                      <div className="flex items-center justify-between gap-2 w-full min-w-0">
                        <span className="truncate min-w-0">
                          {selectedCluster.displayName}
                        </span>
                        <ChevronsUpDown className="shrink-0 opacity-50" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        Select a location
                        <ChevronsUpDown className="ml-auto opacity-50" />
                      </div>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search location..."
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>No location found</CommandEmpty>
                      {recentClusters.length > 1 ? (
                        <CommandGroup
                          heading={
                            <div className="flex items-center gap-1">
                              <History className="size-3.5" />
                              <span>Recently used</span>
                            </div>
                          }
                        >
                          {recentClusters.map((cluster) => {
                            const isSelected = field.value === cluster.id
                            return (
                              <CommandItem
                                className="cursor-pointer"
                                key={`recent-${cluster.id}`}
                                disabled={isSelected}
                                onSelect={() => {
                                  field.onChange(cluster.id)
                                  setOpen(false)
                                }}
                              >
                                {isSuperadmin ? (
                                  <ProviderLogo provider={cluster.provider} />
                                ) : null}
                                {cluster.displayName}
                                <Check
                                  className={cn(
                                    'ml-auto',
                                    isSelected ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            )
                          })}
                        </CommandGroup>
                      ) : null}
                      {groupedClusters.map((group) => {
                        return (
                          <CommandGroup
                            key={group.continent.code}
                            heading={group.continent.title}
                          >
                            {group.clusters.map((cluster) => {
                              const isSelected = field.value === cluster.id
                              return (
                                <CommandItem
                                  className="cursor-pointer"
                                  key={cluster.id}
                                  disabled={isSelected}
                                  onSelect={() => {
                                    field.onChange(cluster.id)
                                    setOpen(false)
                                  }}
                                >
                                  {isSuperadmin ? (
                                    <ProviderLogo provider={cluster.provider} />
                                  ) : null}
                                  {cluster.displayName}
                                  <Check
                                    className={cn(
                                      'ml-auto',
                                      isSelected ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                </CommandItem>
                              )
                            })}
                          </CommandGroup>
                        )
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export const LocationSelector = ({ fieldKey = 'idCluster' }: Props) => {
  return (
    <Suspense fallback={<LocationSelectorFallback />}>
      <LocationSelectorContent fieldKey={fieldKey} />
    </Suspense>
  )
}
